// Single source of truth for material presentation across the site
// (Pricing tabs, Book Now, Sizing/Help-me-pick, Lead form, Footer).
// PRICING lives in ./site-data.js — this file only holds visual + copy meta.

// Canonical display order used EVERYWHERE
export const MATERIAL_ORDER = [
  "Mixed Waste",
  "Shingles",
  "Concrete",
  "Soil / Dirt",
  "Brick",
  "Metal",
];

// Legacy → canonical for URL params and old links
export const LEGACY_CATEGORY_MAP = {
  "Mixed / General Waste": "Mixed Waste",
  "Asphalt Shingles":      "Shingles",
  "Metal Only":            "Metal",
  "Red Bricks":            "Brick",
  "Clean Fill":            "Concrete",
  "Site Test":             "Mixed Waste",
};

export function normalizeMaterial(name) {
  if (!name) return name;
  return LEGACY_CATEGORY_MAP[name] || name;
}

// Materials that qualify for the "clean load" (single-material) discount tier
export const SINGLE_MATERIAL_LOADS = new Set([
  "Concrete", "Soil / Dirt", "Brick", "Metal",
]);

// Slug helper — used to build data-testids and asset keys consistently
export function materialSlug(name) {
  return String(name || "").replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
}

// Per-material presentation. `photo` must exist under /app/frontend/public.
export const MATERIALS = {
  "Mixed Waste": {
    photo: "/waste/mixed-waste.jpg",
    subtitle: "Most popular",
    blurb: "Renovation debris, household junk, demo & general cleanouts.",
    accepts: ["Drywall & wood", "Furniture & junk", "Mixed reno debris", "Garage cleanouts"],
    fallbackBg: "from-slate-700 to-slate-900",
  },
  "Shingles": {
    photo: "/waste/shingles.jpg",
    subtitle: "Roofers' choice",
    blurb: "Tear-offs & re-roof projects. Shingles only — no plywood or insulation.",
    accepts: ["Asphalt shingles", "Roofing felt / underlay", "Roofing nails", "Ridge cap"],
    fallbackBg: "from-stone-700 to-neutral-900",
  },
  "Concrete": {
    photo: "/waste/concrete.jpg",
    subtitle: "Clean-load discount · up to 2 tons",
    blurb: "Broken slabs, walkways & patios. No rebar tangles, no wire mesh.",
    accepts: ["Slabs & sidewalks", "Patio & driveway breakup", "Concrete blocks", "Up to 2 tons included"],
    fallbackBg: "from-slate-500 to-slate-900",
  },
  "Soil / Dirt": {
    photo: "/waste/soil-dirt.jpg",
    subtitle: "Clean-load discount · up to 1 ton",
    blurb: "Excavation & landscaping dirt. No sod, no rocks, no debris.",
    accepts: ["Clean topsoil", "Subsoil from digs", "Garden / landscape soil", "Up to 1 ton included"],
    fallbackBg: "from-amber-800 to-stone-900",
  },
  "Brick": {
    photo: "/waste/brick.jpg",
    subtitle: "Clean-load discount · up to 2 tons",
    blurb: "Red brick & block from chimneys, walls & masonry teardowns.",
    accepts: ["Red brick", "Cinder / concrete block", "Mortar dust", "Up to 2 tons included"],
    fallbackBg: "from-red-800 to-red-950",
  },
  "Metal": {
    photo: "/waste/metal.jpg",
    subtitle: "Clean-load discount · scrap & demo",
    blurb: "Pure scrap metal: appliances, ductwork, framing, rebar.",
    accepts: ["Appliances & HVAC", "Steel framing / rebar", "Copper, brass, aluminum", "Old tools & fixtures"],
    fallbackBg: "from-zinc-700 to-slate-900",
  },
};

// Prohibited items — shown wherever material selection is offered
export const NOT_ACCEPTED = [
  "Paint, solvents & adhesives",
  "Chemicals & hazardous liquids",
  "Batteries (car, lithium, alkaline)",
  "Tires",
  "Propane / pressurized tanks",
  "Asbestos",
  "Medical & biohazard waste",
  "Electronics (TVs, monitors)",
  "Refrigerators with refrigerant",
  "Oils, fuels & gasoline",
];

// Sizing-guide recommendation tables — feeds the "Help me pick" wizard
export const JOB_TYPES = [
  {
    key: "roofing",
    label: "Roofing",
    hint: "Shingle tear-offs & re-roofs",
    material: "Shingles",
    options: [
      { label: "30 bundles or less",  size: "6 yd³"  },
      { label: "30 – 90 bundles",     size: "10 yd³" },
      { label: "90 – 120 bundles",    size: "14 yd³" },
      { label: "120 – 160 bundles",   size: "16 yd³" },
    ],
  },
  {
    key: "renovation",
    label: "General Renovation",
    hint: "Mixed household reno debris",
    material: "Mixed Waste",
    options: [
      { label: "1 bathroom",                                          size: "6 yd³"  },
      { label: "Small kitchen or 1 room reno",                         size: "10 yd³" },
      { label: "2 rooms / 1 regular or large kitchen reno",            size: "14 yd³" },
      { label: "3 rooms / 2 rooms + 1 bath / 1 room + 1 kitchen + 1 bath", size: "16 yd³" },
      { label: "Full level (main floor or basement)",                  size: "20 yd³" },
    ],
  },
  {
    key: "single",
    label: "Single-material load",
    hint: "Concrete, soil, brick or metal only — unlocks lower price",
    material: null, // material picked inside sub-option
    options: [
      { label: "Soil / Dirt",   size: "6 yd³", material: "Soil / Dirt" },
      { label: "Concrete",      size: "6 yd³", material: "Concrete"   },
      { label: "Brick",         size: "6 yd³", material: "Brick"      },
      { label: "Metal only",    size: "6 yd³", material: "Metal"      },
    ],
  },
];

/**
 * Find the tonnage for a material + size using the PRICING table.
 * Used to compose the "14 yd — Shingles — Max 2 tons — $XXX" summary line.
 */
export function findTons(pricingTable, material, size) {
  const bins = pricingTable?.[material] || [];
  const bin = bins.find((b) => b.size === size);
  if (!bin || !bin.tiers?.length) return null;
  return bin.tiers[0].tons ?? null;
}

/**
 * Compose the standard "14 yd — Shingles — Max N tons — $XXX" summary line.
 */
export function formatSelectionLine(pricingTable, material, size, tierIndex = 0) {
  const bins = pricingTable?.[material] || [];
  const bin = bins.find((b) => b.size === size);
  if (!bin || !bin.tiers?.[tierIndex]) return `${size} — ${material}`;
  const t = bin.tiers[tierIndex];
  const tonsPart = t.tons ? `Max ${t.tons} ton${t.tons > 1 ? "s" : ""}` : "Flat rate";
  return `${size} — ${material} — ${tonsPart} — $${t.price}`;
}
